/**
 * sharedTokenRefresh — single source of truth for OAuth access-token refresh
 * across every axios client in the app.
 *
 * Why this exists:
 *
 * Each axios client (Client / AuthenticatedClient /
 * AuthenticatedScrumbleApiClient / AuthenticatedDownloadClient) used to
 * carry its OWN `isRefreshing` flag + subscriber queue. When the cockpit
 * homepage mounts ~10 parallel requests across multiple clients and every
 * one of them sees a 401 (e.g. when the access token has just expired),
 * each client INDEPENDENTLY fired a refresh against `/auth/proxy/refresh`.
 *
 * Most OAuth providers (Scrumble included) rotate the refresh token on
 * every refresh call. So:
 *
 *   1. Client A's refresh succeeds → server returns new access + new
 *      refresh tokens. Server invalidates the old refresh token.
 *   2. Client B's refresh, fired moments later with the now-invalidated
 *      old refresh token, gets a 401.
 *   3. Client B's catch branch runs: clear localStorage, clear
 *      sessionStorage, redirect to `/#/login?session_expired=true&redirect=...`.
 *   4. The page reloads. Tokens were cleared by step 3 — the user has to
 *      log in again. After login, step 1 happens again and we loop.
 *
 * The fix is the textbook single-flight pattern: collapse N concurrent
 * refresh attempts into one HTTP call, where every caller subscribes to
 * the same outcome. This module owns the in-flight Promise so all four
 * clients share it through a single import.
 *
 * Contract:
 *   - `refreshAccessToken()` returns a Promise<string> with the new access
 *     token (already written to sessionStorage when it resolves).
 *   - On failure it rejects with the underlying error, AND clears the
 *     access + refresh tokens from storage. Callers can then redirect to
 *     `/#/login?session_expired=true&redirect=...` themselves (intentionally outside the
 *     promise so the choice of *what to do on auth failure* stays with the
 *     interceptor — page reload vs in-app banner vs silent retry).
 *   - Multiple concurrent calls share one HTTP request; the returned
 *     Promise is the SAME instance for all callers in a single refresh
 *     cycle, then released so the next 401 burst can refresh again.
 *   - This module deliberately does NOT install any axios interceptors —
 *     it only exposes the primitives. The interceptors live in each
 *     client and call into this module.
 */

import { isSafeRedirectPath } from '@/utils/authRedirect';

let inflight = null;
let lastRefreshedAt = 0;
// Minimum gap between refreshes. Acts as a safety net so a fast-failing
// refresh can't drive a tight retry loop into the backend. Tuned to match
// the auth proxy's per-IP rate-limit window.
const MIN_REFRESH_GAP_MS = 2000;

/**
 * Trigger (or join) a token refresh. Returns the new access token on
 * success. Rejects with the underlying error on failure (after clearing
 * storage so the user is genuinely logged out and a re-login can succeed).
 *
 * The actual HTTP call goes through `AuthRepository.refreshToken` so this
 * module doesn't need to know the refresh URL or payload shape — that
 * detail stays with the auth provider switch in AuthRepository.
 *
 * @returns {Promise<string>} the new access token
 */
export function refreshAccessToken({ rejectedAccessToken = null } = {}) {
  if (inflight) return inflight;

  const currentToken = window.sessionStorage.getItem('accessToken');
  // A late 401 for an older request can reuse a token another request already refreshed.
  if (rejectedAccessToken && currentToken && currentToken !== rejectedAccessToken) {
    return Promise.resolve(currentToken);
  }

  // Throttle: never less than MIN_REFRESH_GAP_MS between two REAL refreshes.
  // We still serve a successful in-flight to anyone who joined; this only
  // gates *starting* a brand new refresh after a recent success, unless that
  // exact token was rejected (a billing update can revoke it immediately).
  const sinceLast = Date.now() - lastRefreshedAt;
  if (sinceLast >= 0 && sinceLast < MIN_REFRESH_GAP_MS && lastRefreshedAt > 0 && currentToken !== rejectedAccessToken) {
    // Re-read the token that the last refresh already wrote; treat as a
    // synchronous hit. If somehow the token is gone, fall through to a
    // proper refresh.
    const fresh = window.sessionStorage.getItem('accessToken');
    if (fresh) return Promise.resolve(fresh);
  }

  inflight = (async () => {
    try {
      // Lazy import: AuthRepository pulls in i18n and the other clients;
      // a static import here would create a hard module cycle.
      const { default: AuthRepository } = await import('@/repositories/AuthRepository');
      const refreshToken = window.localStorage.getItem('refreshToken');
      if (!refreshToken) {
        // No active session at all (clean app boot before login, or
        // post-logout). Surface this as a distinct error so the interceptor
        // can decide NOT to redirect — there's no "session expired", there's
        // just no session yet. Returning a regular Error would cause the
        // caller to bounce to /#/login?session_expired=true and, when the
        // user is already there, kick off an infinite reload loop.
        const err = new Error('No refresh token available');
        err.code = 'NO_SESSION';
        throw err;
      }
      const response = await AuthRepository.refreshToken(refreshToken);
      const accessToken = response?.data?.access_token;
      const newRefreshToken = response?.data?.refresh_token;
      if (!accessToken) {
        throw new Error('Refresh response missing access_token');
      }
      window.sessionStorage.setItem('accessToken', accessToken);
      // OAuth providers commonly rotate the refresh token on use. Persist
      // the new one when present so the next refresh has a valid token.
      if (newRefreshToken) {
        window.localStorage.setItem('refreshToken', newRefreshToken);
      }
      lastRefreshedAt = Date.now();
      return accessToken;
    } catch (err) {
      // Clear both tokens — the refresh failed, treat the session as gone.
      // Callers decide what to do next (redirect, surface a message, etc.).
      window.sessionStorage.removeItem('accessToken');
      window.localStorage.removeItem('refreshToken');
      throw err;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/** Test/logout hook — wipe in-memory state. */
export function _resetSharedRefreshState() {
  inflight = null;
  lastRefreshedAt = 0;
}

/**
 * True iff the user is currently sitting on the /login route (either
 * path-based or hash-based, since the SPA uses `createWebHashHistory`).
 * Used by every interceptor to avoid the redirect-reload loop where
 * redirecting back to login re-mounts App.vue, which fires the
 * same authenticated loaders again, which 401, which redirect again, …
 *
 * Centralised here so every client agrees on what "already on login"
 * means — otherwise one client redirects, the others don't, and we still
 * loop via the inconsistent client.
 */
export function isOnLoginRoute() {
  const hash = window.location.hash || '';
  const pathname = window.location.pathname || '';

  // Hash routing means `/#/tools` is the real SPA route. If a previous
  // full-page redirect produced `/login?session_expired=true#/tools`, the
  // pathname looks like login but the mounted route is still `/tools`.
  if (hash.startsWith('#/')) {
    return hash.startsWith('#/login');
  }

  return (
    pathname === '/login' ||
    pathname.startsWith('/login/') ||
    pathname.startsWith('/login?')
  );
}

/**
 * True on a public portal page (`#/public/...`, see src/router/routes/public.js). Those pages
 * are read without an account; a stale session there must be dropped, not bounced to /login.
 */
export function isOnPublicRoute() {
  return (window.location.hash || '').startsWith('#/public/');
}

function getCurrentInAppRedirectPath() {
  const hash = window.location.hash || '';
  const pathname = window.location.pathname || '/';
  const search = window.location.search || '';

  const candidate = hash.startsWith('#/') ? hash.slice(1) : `${pathname}${search}`;
  return isSafeRedirectPath(candidate) ? candidate : null;
}

export function buildSessionExpiredLoginHref() {
  const params = new URLSearchParams({ session_expired: 'true' });
  const redirectPath = getCurrentInAppRedirectPath();

  if (redirectPath) {
    params.set('redirect', redirectPath);
  }

  return `/#/login?${params.toString()}`;
}

/**
 * Safe redirect to login with the current in-app route preserved as
 * `redirect`. Only fires when the user is not already on /login — otherwise
 * it would reload the app and re-trigger the very 401 that brought us here.
 */
export async function redirectToLoginOnSessionExpired() {
  if (isOnLoginRoute()) return;

  // Hash-only redirects (74fa482c) don't reload the page, so nothing else
  // resets Pinia state: without this, the router's auth guard reads a stale
  // isAuthenticated=true on the very next beforeEach it runs for the login
  // navigation this triggers, and bounces straight back to the authenticated
  // landing page. Mirror useSessionStore.clearUserData() here since this is
  // the one chokepoint all refresh-failure callers already share — lazy
  // import, same pattern as AuthRepository above, to dodge the store's own
  // heavier dependency chain.
  const { useSessionStore } = await import('@/stores/useSessionStore');
  useSessionStore().clearUserData();

  // A reader of a public page (a shared newsroom article) whose old session expired keeps
  // reading anonymously: the page needs no account, and a login screen would hide it.
  if (isOnPublicRoute()) return;

  window.location.href = buildSessionExpiredLoginHref();
}
